import React from 'react';
import countriesData from '../../data/countries.json';
import territoriesData from '../../data/territories.json';

/** Small pieces shared by the shared-trip page and its tabs. */

const PUBLIC = process.env.PUBLIC_URL || '';

// Mirrors the app's app/utils/countryName.ts: every place that owns a flag, countries first and
// territories after (Puerto Rico, Hong Kong, Greenland…), so a stop in San Juan shows PR's flag.
// territories.json is a copy of the app's app/data/territories.json; keep the two in step.
const COUNTRY_CODES = (() => {
  const out = {};
  for (const c of [...(countriesData.countries || []), ...(territoriesData.territories || [])]) {
    const name = c.name.toLowerCase();
    if (!out[name]) out[name] = c.code.toLowerCase();
  }
  return out;
})();

// Names Google returns that differ from ours (the app's COUNTRY_NAME_ALIASES).
const ALIASES = {
  türkiye: 'turkey',
  turkiye: 'turkey',
  czechia: 'czech republic',
  uk: 'united kingdom',
  gb: 'united kingdom',
  'great britain': 'united kingdom',
  usa: 'united states',
  'u.s.a.': 'united states',
  'u.s.': 'united states',
  'united states of america': 'united states',
  macao: 'macau',
  curacao: 'curaçao',
  'turks & caicos islands': 'turks and caicos islands',
  'us virgin islands': 'u.s. virgin islands',
  'united states virgin islands': 'u.s. virgin islands',
};

const codeOfName = (n) => COUNTRY_CODES[ALIASES[n] || n];

/** "Lisbon, Portugal" → "pt"; mirrors getCountryCode in the app's TripTimelineView. */
export function countryCodeOf(name) {
  if (!name) return null;
  const direct = codeOfName(name.trim().toLowerCase());
  if (direct) return direct;
  const parts = name.includes(',') ? name.split(',') : name.split('-');
  const last = (parts[parts.length - 1] || '').trim().toLowerCase();
  if (!last) return null;
  if (codeOfName(last)) return codeOfName(last);
  const fuzzy = Object.keys(COUNTRY_CODES).find((n) => n.includes(last) || last.includes(n));
  return fuzzy ? COUNTRY_CODES[fuzzy] : null;
}

export const Flag = ({ name, code, className = 'si-flag' }) => {
  const c = code ? code.toLowerCase() : countryCodeOf(name);
  return (
    <img
      className={className}
      src={`${PUBLIC}/assets/images/flags/${c || 'default-flag'}.webp`}
      onError={(e) => { e.currentTarget.src = `${PUBLIC}/assets/images/flags/default-flag.webp`; }}
      alt=""
    />
  );
};

/** An SVG from /assets/icons drawn in the current text color (CSS mask), like the app's SVG icons. */
export const MaskIcon = ({ name, size }) => (
  <span
    className="si-mask-icon"
    style={{
      width: size,
      height: size,
      WebkitMaskImage: `url(${PUBLIC}/assets/icons/${name}.svg)`,
      maskImage: `url(${PUBLIC}/assets/icons/${name}.svg)`,
    }}
  />
);

/**
 * The server's day list folded back into the app timeline's shape: stays and transits in order,
 * each with its day visits by offset (0 = arrival day).
 */
export function buildSegments(days) {
  const segs = [];
  let i = 0;
  while (i < days.length) {
    const d = days[i];
    if (d.stop && d.stop.night === 1) {
      const len = Math.max(1, d.stop.nights);
      segs.push({
        kind: 'stay', start: i, len, nights: d.stop.nights, name: d.stop.name, photoUrl: d.stop.photoUrl,
        id: d.stop.id || null, lat: d.stop.lat ?? null, lng: d.stop.lng ?? null,
      });
      i += len;
    } else if (d.inTransit) {
      let len = 0;
      while (days[i + len] && days[i + len].inTransit && !days[i + len].stop) len += 1;
      segs.push({ kind: 'transit', start: i, len: Math.max(1, len), nights: Math.max(1, len) });
      i += Math.max(1, len);
    } else {
      i += 1;
    }
  }
  return segs;
}
